import type {
  NeoConversationHistoryDTO,
  NeoConversationRichContentPart,
} from "../../../hexagon/core/ports/driven/neoConversationRepository";
import { neoArtifactsFromToolOutput } from "../../../pages/Neo/artifacts";
import { APP_CONTEXT_PATTERN } from "../../neo/appContext";
import {
  mergeRichContent,
  richContentFromParts,
  textFromContentParts,
} from "./richContent";
import { noticeForTerminalRow } from "./turnNotices";
import type {
  CopilotMessage,
  CopilotToolCall,
  CopilotToolStatus,
} from "./types";

export const toolStatus = (value: unknown): CopilotToolStatus => {
  const status = String(value ?? "completed");
  return status === "pending" ||
    status === "running" ||
    status === "completed" ||
    status === "failed" ||
    status === "cancelled" ||
    status === "skipped"
    ? status
    : "completed";
};

export const historyMessageId = (backendId: number): string =>
  `message-${backendId}`;

/** Backend id behind a ``message-<id>`` local id, if it has one. */
export const backendMessageId = (localId: string): number | null => {
  const match = localId.match(/^message-(\d+)$/);
  return match ? Number(match[1]) : null;
};

export interface MappedHistory {
  title: string;
  messages: CopilotMessage[];
  latestSequenceNum: number;
  /** Assistant row the server is still generating (refresh mid-turn). */
  inflightAssistantId: number | null;
}

export const mapConversationHistory = (
  data: NeoConversationHistoryDTO
): MappedHistory => {
  const toolsByAssistant = new Map<number, CopilotToolCall[]>();
  const richContentByAssistant = new Map<
    number,
    NeoConversationRichContentPart[]
  >();
  data.toolCalls.forEach((tool) => {
    const items = toolsByAssistant.get(tool.assistant_message_id) ?? [];
    items.push({
      id: tool.tool_call_id,
      rowId: tool.id,
      name: tool.tool_name,
      status: toolStatus(tool.status),
      input: tool.tool_input_json,
      output: tool.tool_output_json,
      durationMs: tool.duration_ms ?? undefined,
      isError: Boolean(tool.error_json),
    });
    toolsByAssistant.set(tool.assistant_message_id, items);
    const artifacts = neoArtifactsFromToolOutput(tool.tool_output_json).map(
      ({ part }) => part as NeoConversationRichContentPart
    );
    richContentByAssistant.set(
      tool.assistant_message_id,
      mergeRichContent(
        richContentByAssistant.get(tool.assistant_message_id),
        artifacts
      )
    );
  });

  const rows = [...data.messages]
    .sort((left, right) => left.sequence_num - right.sequence_num)
    .filter(
      (message) => message.role === "user" || message.role === "assistant"
    );
  const last = rows[rows.length - 1];
  const inflightAssistantId =
    last?.role === "assistant" &&
    (last.status === "pending" || last.status === "streaming")
      ? last.id
      : null;

  const messages = rows.map((message): CopilotMessage => {
    // Hidden context (``<app_context>``) rides in its own text part; it is
    // for the model, never the bubble.
    const persistedText = textFromContentParts(message.content_parts)
      .replace(APP_CONTEXT_PATTERN, "")
      .trim();
    const isAssistant = message.role === "assistant";
    const inflight = message.id === inflightAssistantId;
    return {
      id: historyMessageId(message.id),
      role: message.role as "user" | "assistant",
      content:
        typeof message.content === "string" && message.content.trim()
          ? message.content
          : persistedText,
      clientMessageId:
        !isAssistant && message.client_message_id
          ? message.client_message_id
          : undefined,
      richContent: isAssistant
        ? mergeRichContent(
            richContentByAssistant.get(message.id),
            richContentFromParts(message.content_parts)
          )
        : undefined,
      attachments: !isAssistant
        ? (message.content_parts ?? []).flatMap((part) =>
            (part.type === "document" || part.type === "image") &&
            part.source.type === "neo_file"
              ? [
                  {
                    id: part.source.neo_file_id,
                    name: part.title || "Attachment",
                    mimeType:
                      part.type === "image" ? "image/unknown" : undefined,
                  },
                ]
              : []
          )
        : undefined,
      reasoning:
        isAssistant &&
        (inflight || message.reasoning || message.reasoning_duration_ms)
          ? {
              content: message.reasoning ?? "",
              durationMs: message.reasoning_duration_ms ?? undefined,
              active: inflight,
            }
          : undefined,
      tools: isAssistant ? toolsByAssistant.get(message.id) ?? [] : undefined,
      truncated: isAssistant && message.finish_reason === "length",
      ...(isAssistant ? noticeForTerminalRow(message) ?? {} : {}),
    };
  });

  return {
    title: data.conversation.title?.trim() || "Untitled conversation",
    messages,
    latestSequenceNum: data.messages.reduce(
      (latest, message) => Math.max(latest, message.sequence_num),
      0
    ),
    inflightAssistantId,
  };
};
